import React, { useState, useEffect, useCallback } from "react";
import cx from "classnames";
import { useTranslationContext } from "@providers/TranslationProvider";
import { useRouteContext } from "@providers/RouteProvider";
import {
  hasConsentChoice,
  getConsent,
  setConsent,
  acceptAll,
  rejectAll,
  type CookieConsentState,
} from "@utils/cookieConsent";
import config from "@utils/config";
import TagManager from "react-gtm-module";
import styles from "./CookieConsent.module.scss";

export default function CookieConsent() {
  const { i18n } = useTranslationContext();
  const { router } = useRouteContext();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookieConsentState>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  const privacyUrl = router.toUrl("privacy", {
    legalNoticeType: "PrivacyPolicy",
  });

  const checkAndShow = useCallback(() => {
    if (!hasConsentChoice()) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    checkAndShow();
  }, [checkAndShow]);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    if (config.GTM_ID) {
      TagManager.initialize({ gtmId: config.GTM_ID });
    }
  };

  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    // GTM not loaded - user rejected tracking
  };

  const handleOpenPreferences = () => {
    const stored = getConsent();
    if (stored) {
      setPreferences(stored.consent);
    }
    setShowPreferences(true);
  };

  const handleSavePreferences = () => {
    const consent: CookieConsentState = {
      necessary: true,
      functional: preferences.functional,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
    };
    setConsent(consent);
    setShowPreferences(false);
    setVisible(false);

    if (consent.analytics || consent.marketing) {
      if (config.GTM_ID) {
        TagManager.initialize({ gtmId: config.GTM_ID });
      }
    }
  };

  // Listen for footer "Cookie Preferences" link click
  useEffect(() => {
    const handler = () => {
      const stored = getConsent();
      if (stored) {
        setPreferences(stored.consent);
      }
      setShowPreferences(true);
    };
    window.addEventListener("openCookiePreferences", handler);
    return () => window.removeEventListener("openCookiePreferences", handler);
  }, []);

  if (!visible && !showPreferences) return null;

  if (showPreferences) {
    return (
      <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="cookie-preferences-title">
        <div className={styles.modal}>
          <h2 id="cookie-preferences-title" className={styles.modalTitle}>
            {i18n.t("CookieConsent.preferencesTitle")}
          </h2>
          <p className={styles.modalDescription}>
            {i18n.t("CookieConsent.preferencesDescription")}{" "}
            <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
              {i18n.t("CookieConsent.privacyPolicy")}
            </a>
          </p>

          <div className={styles.category}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryName}>
                {i18n.t("CookieConsent.categoryNecessary")}
              </span>
              <span className={styles.categoryRequired}>
                {i18n.t("CookieConsent.alwaysRequired")}
              </span>
            </div>
            <p className={styles.categoryDescription}>
              {i18n.t("CookieConsent.categoryNecessaryDesc")}
            </p>
          </div>

          <div className={styles.category}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryName}>
                {i18n.t("CookieConsent.categoryFunctional")}
              </span>
              <button
                type="button"
                className={cx(styles.toggle, preferences.functional && styles.toggleActive)}
                onClick={() => setPreferences((p) => ({ ...p, functional: !p.functional }))}
                aria-label={i18n.t("CookieConsent.toggleLabel")}
              >
                <span
                  className={cx(
                    styles.toggleKnob,
                    preferences.functional && styles.toggleKnobActive
                  )}
                />
              </button>
            </div>
            <p className={styles.categoryDescription}>
              {i18n.t("CookieConsent.categoryFunctionalDesc")}
            </p>
          </div>

          <div className={styles.category}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryName}>
                {i18n.t("CookieConsent.categoryAnalytics")}
              </span>
              <button
                type="button"
                className={cx(styles.toggle, preferences.analytics && styles.toggleActive)}
                onClick={() => setPreferences((p) => ({ ...p, analytics: !p.analytics }))}
                aria-label={i18n.t("CookieConsent.toggleLabel")}
              >
                <span
                  className={cx(
                    styles.toggleKnob,
                    preferences.analytics && styles.toggleKnobActive
                  )}
                />
              </button>
            </div>
            <p className={styles.categoryDescription}>
              {i18n.t("CookieConsent.categoryAnalyticsDesc")}
            </p>
          </div>

          <div className={styles.category}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryName}>
                {i18n.t("CookieConsent.categoryMarketing")}
              </span>
              <button
                type="button"
                className={cx(styles.toggle, preferences.marketing && styles.toggleActive)}
                onClick={() => setPreferences((p) => ({ ...p, marketing: !p.marketing }))}
                aria-label={i18n.t("CookieConsent.toggleLabel")}
              >
                <span
                  className={cx(
                    styles.toggleKnob,
                    preferences.marketing && styles.toggleKnobActive
                  )}
                />
              </button>
            </div>
            <p className={styles.categoryDescription}>
              {i18n.t("CookieConsent.categoryMarketingDesc")}
            </p>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSavePreferences}
            >
              {i18n.t("CookieConsent.savePreferences")}
            </button>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => setShowPreferences(false)}
            >
              {i18n.t("CookieConsent.cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.banner} role="dialog" aria-modal="true" aria-labelledby="cookie-banner-title">
      <div className={styles.bannerInner}>
        <p id="cookie-banner-title" className={styles.message}>
          {i18n.t("CookieConsent.message")}{" "}
          <a href={privacyUrl} target="_blank" rel="noopener noreferrer">
            {i18n.t("CookieConsent.privacyPolicy")}
          </a>
        </p>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.acceptButton}
            onClick={handleAcceptAll}
          >
            {i18n.t("CookieConsent.acceptAll")}
          </button>
          <button
            type="button"
            className={styles.rejectButton}
            onClick={handleRejectAll}
          >
            {i18n.t("CookieConsent.rejectAll")}
          </button>
          <button
            type="button"
            className={styles.preferencesLink}
            onClick={handleOpenPreferences}
          >
            {i18n.t("CookieConsent.managePreferences")}
          </button>
        </div>
      </div>
    </div>
  );
}
